<template>
  <div
    v-if="!fullscreen"
    class="navbar shadow-lg text-bg-berry"
  >
    <div class="container-lg">
      <div class="navbar-brand">
        <RouterLink to="/">
          <img
            src="@/assets/logo.svg"
            :alt="t('common.logoAlt')"
            id="logo"
          />
        </RouterLink>
      </div>
      <div class="me-auto">
        <select
          @change="navigate"
          v-model="selected"
          v-if="link_options?.length > 1"
          class="select-service"
        >
          <option
            value="rtms"
            v-if="link_options?.includes('rtms')"
            disabled
          >
            RTMS
          </option>
          <option
            value="ltms"
            v-if="link_options?.includes('ltms')"
          >
            LTMS
          </option>
          <option
            value="qems"
            v-if="link_options?.includes('qems')"
          >
            QEMS
          </option>
          <option
            value="qdms"
            v-if="link_options?.includes('qdms')"
          >
            QDMS
          </option>
        </select>
      </div>
      <div class="hstack gap-2">
        <DebugLocaleSwitcher />
        <ConnIndicator />
        <MessagesIndicator />
        <AuditTrail />
        <RouterLink
          :to="{ name: 'profile.item' }"
          :active-class="''"
          :title="t('common.cabinetTitle')"
        >
          <button
            type="button"
            class="btn btn-outline-light btn-sm"
          >
            <i class="bi bi-person me-1"></i>
            {{ user.fio || user.username }}
          </button>
        </RouterLink>
        <FullscreenButton class="btn btn-sm btn-outline-secondary" />
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="logout"
        >
          <i class="bi bi-power"></i>
        </button>
      </div>
    </div>
  </div>
  <div
    class="py-3"
    :class="layoutClasses"
  >
    <div :class="{ row: !fullscreen }">
      <div
        id="fullscreen-floating"
        class="sticky-top float-end fullscreen-floating-bar hstack gap-2 align-items-center"
        v-if="fullscreen"
      >
        <template v-if="route.name === 'orders.list' || route.name === 'tickets.list'">
          <button
            class="btn btn-outline-secondary btn-icon-square btn-orders-view"
            :class="{ active: route.hash === '#table' }"
            type="button"
            @click="router.push({ hash: '#table' })"
          >
            <i
              class="bi bi-layout-three-columns"
              style="display: inline-block; transform: rotate(90deg)"
            ></i>
          </button>
          <button
            class="btn btn-outline-secondary btn-icon-square btn-orders-view"
            type="button"
            :class="{ active: route.hash === '#kanban' }"
            @click="router.replace({ hash: '#kanban' })"
          >
            <i class="bi bi-layout-three-columns"></i>
          </button>
        </template>
        <FullscreenButton class="btn btn-outline-secondary btn-icon-square" />
      </div>
      <Sidebar
        class="col-3"
        v-if="!fullscreen"
      />
      <main class="main">
        <slot />
      </main>
    </div>
  </div>
</template>
<style>
#fullscreen-floating {
  width: fit-content;
}
</style>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import config from '@/config'
import { useApi } from '@/composables/api'
import { useUserStore } from '@/stores/user'
import { useLockStore } from '@/stores/lock'
import { useUiStore } from '@/stores/ui'
import Sidebar from '@/components/sidebar/sidebar.vue'
import AuditTrail from '@/components/history/AuditTrail.vue'
import ConnIndicator from '@/components/connIndicator.vue'
import { useInactivityTracker } from '@/composables/itracker'
import FullscreenButton from '@/components/fullscreenButton.vue'
import DebugLocaleSwitcher from '@/components/debug/DebugLocaleSwitcher.vue'
import MessagesIndicator from '@/views/messages/indicator.vue'
import { useRouter, useRoute } from 'vue-router'
import { useConfirm } from '@/composables/ui'

const { t } = useI18n()
const { refresh: getLogoutLink, model: logoutLink } = useApi({ url: 'logout' })
const { user } = storeToRefs(useUserStore())
const lockStore = useLockStore()
const { lockKeys } = storeToRefs(lockStore)
const uiStore = useUiStore()
const { fullscreen, returnUrl, isLoggingOut } = storeToRefs(uiStore)
const layoutClasses = computed(() => ({
  'layout-fullscreen': fullscreen.value,
  'container-lg': !fullscreen.value,
  'container-fluid': fullscreen.value,
}))
const router = useRouter()
const route = useRoute()
const continueConfirm = useConfirm(() => t('common.unsavedContinue'))

async function logout(confirmExit = true) {
  isLoggingOut.value = true
  if (Object.keys(lockKeys.value).length > 0) {
    if (confirmExit && !(await continueConfirm())) {
      isLoggingOut.value = false
      return
    }
    await lockStore.resetAll()
  }
  if (user.value.sso) {
    await getLogoutLink()
    window.location.assign(logoutLink.value.logout_uri)
  } else {
    logoutApi()
  }
  returnUrl.value = undefined
  user.value = undefined
}

useInactivityTracker(() => logout(false))

const { refresh: logoutApi } = useApi({
  url: 'logout',
  method: 'get',
})

const selected = ref('rtms')

const { model: redirect, refresh: redirectApi } = useApi({
  url: 'sso_redirect',
  method: 'post',
})

const { model: link_options, refresh: requestLinkOptions } = useApi({
  url: 'user_services',
  immediate: true,
  pick: d => d.services || [],
})

onMounted(async () => {
  requestLinkOptions()
})

/** Явный URL модуля из runtime/config, иначе origin с подменой поддомена сервиса. */
function getHost(service) {
  const key = {
    rtms: 'VITE_RTMS_URL',
    ltms: 'VITE_LTMS_URL',
    qems: 'VITE_QEMS_URL',
    qdms: 'VITE_QDMS_URL',
  }[service]
  if (!key) return undefined
  const direct = window.__RUNTIME__?.[key] || config[key]
  if (direct) return String(direct).replace(/\/$/, '')
  const seed =
    window.__RUNTIME__?.VITE_RTMS_URL ||
    config.VITE_RTMS_URL ||
    window.__RUNTIME__?.VITE_API_URL ||
    config.VITE_API_URL ||
    window.location.origin
  try {
    const u = new URL(seed)
    u.hostname = u.hostname.replace(
      /^(rtms|qems|qdms|ltms|sso|catalogs|docparser)(?=\.)/i,
      service,
    )
    if (!new RegExp(`^${service}(\\.|$)`, 'i').test(u.hostname)) {
      u.hostname = u.hostname.replace(/^[^.:]+/, service)
    }
    return u.origin
  } catch {
    return undefined
  }
}

async function navigate(event) {
  const service = event?.target?.value || selected.value
  if (!service || service === 'rtms') {
    selected.value = 'rtms'
    return
  }
  const host = getHost(service)
  if (!host) {
    selected.value = 'rtms'
    console.error(`[service-switch] no URL for "${service}"`)
    return
  }
  selected.value = service
  try {
    await redirectApi({ query: { url: host, service } })
    window.location.href = redirect.value.url
  } catch (error) {
    selected.value = 'rtms'
    throw new Error('Redirect request failed:', error)
  }
}
</script>
